import { Link } from 'react-router-dom';
import { ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import { removeQueuedCapture } from '@/lib/offline';
import { stateLine } from '@/lib/queue-status';
import { getPlatformDegradation } from '@/lib/platform';

const STATUS_TONE = {
  pending: 'neutral',
  uploading: 'brand',
  uploaded: 'success',
  failed: 'danger',
} as const;

const STATUS_LABEL = {
  pending: 'Queued',
  uploading: 'Uploading',
  uploaded: 'Sent',
  failed: 'Failed',
} as const;

/**
 * CA-07: Offline queue (wireframes/capture-app.html #ca07). Every
 * capture on this screen lives in IndexedDB (src/lib/offline/db.ts), so
 * it survives a reload; this list simply reads that store back. This is
 * the resilience control SC3 note 1 requires, made visible.
 *
 * It is also the only way into CA-05, the extraction result. A capture is
 * enqueued locally and only earns a captureId when the queue flushes and the
 * API answers, so the shutter cannot navigate to a result that does not
 * exist yet. Until this screen linked them, nothing in the app reached
 * `/capture/:captureId/result` and the operator never saw what the system
 * read from their photograph.
 */
export function OfflineQueue() {
  const { queue, online, flushNow, refresh } = useOfflineQueue();
  const degradation = getPlatformDegradation();

  return (
    <div className="flex flex-col gap-4">
      {/* The shell header already carries the connection badge, so this
          screen states the queue and nothing else. */}
      <h1 className="text-base font-semibold">Upload queue</h1>

      {queue.length === 0 && <p className="text-sm text-muted">Nothing queued.</p>}

      <div className="grid gap-2 sm:grid-cols-2">
        {queue.map((item) => (
          <Card key={item.localId} className="p-3 sm:p-3">
            <CardContent className="flex items-center justify-between gap-3 p-0 text-sm">
              <div className="min-w-0">
                <p className="text-surface-foreground">
                  {item.mode} capture, {new Date(item.createdAt).toLocaleTimeString()}
                </p>
                <p className="text-xs text-muted">{stateLine(item, online)}</p>
                {item.lastError && (
                  <p className="mt-0.5 text-xs text-danger">{item.lastError}</p>
                )}
                {item.refusal && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="mt-1 min-h-11"
                    onClick={() => {
                      void removeQueuedCapture(item.localId).then(refresh);
                    }}
                  >
                    <Trash2 />
                    Discard
                  </Button>
                )}
                {item.status === 'failed' && !item.refusal && (
                  <Link
                    to="/flagged"
                    className="mt-1 inline-flex min-h-11 items-center gap-1 text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    Retry it or discard it
                    <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
                  </Link>
                )}
                {item.captureId && (
                  <Link
                    to={`/capture/${item.captureId}/result`}
                    className="mt-1 inline-flex min-h-11 items-center gap-1 text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    See what was read
                    <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
                  </Link>
                )}
              </div>
              <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-muted sm:max-w-md">
        Captures stay on this device until upload confirms.
        {!degradation.backgroundSync && ' Keep the app open until the queue is empty on this device.'}
      </p>

      <Button
        variant="outline"
        className="sm:max-w-xs"
        onClick={() => void flushNow()}
        disabled={!online}
      >
        Retry upload now
      </Button>
    </div>
  );
}
